import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { format, parseISO, startOfDay, endOfDay } from 'date-fns';
import { useStores } from '@/hooks';
import { AppTextarea, AppSingleSelect, AppMultiSelect, AppDatePicker, AppButton, AppTimePicker, AppAlert } from '@pro-sto/ui';
import {
  getWorkingHoursForDate,
  parseTimeToMinutes,
  formatMinutesToTime,
  buildTimeSlots,
} from '@/utils/scheduleHelpers';
import { ClientPhoneField, formatPhoneWithMask, formatClientName, isPhoneComplete, findClientByExactPhone } from '@/features/clients';
import { CarField } from '@/features/cars';
import { detectClientOverlapOnOtherPost, getServicesDurationMinutes } from '@/features/bookings/clientOverlap';
import { getBookingPostUuid } from '@/features/bookings/getBookingPostUuid';
import { fetchBookingsInRange, mergeBookingsByUuid } from '@/features/bookings/fetchBookingsInRange';
import type { SelectOption } from '@pro-sto/ui';
import type { SelectOption as MultiSelectOption } from '@pro-sto/ui';
import type { SelectOption as AutocompleteOption } from '@pro-sto/ui';
import type { ClientSearchResultDto, CarSearchResultDto } from '@pro-sto/api-client';
import type { BookingFormProps, BookingFormFieldName } from './BookingForm.types';
import './BookingForm.css';

// Безопасное извлечение строки из unknown-полей API
const safeString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return String(value);
};

const extractLicensePlate = (lp: unknown): string => {
  if (typeof lp === 'string') return lp;
  if (typeof lp === 'object' && lp !== null) {
    const plate = lp as Record<string, unknown>;
    const num = safeString(plate.number);
    const region = safeString(plate.region);
    return region ? `${num} ${region}` : num;
  }
  return '';
};

const BookingForm: React.FC<BookingFormProps> = observer(({
  isOpen,
  onClose,
  mode,
  title,
  submitLabel,
  headerExtra,
  onSubmit,
  readonlyFields = new Set<BookingFormFieldName>(),
  booking,
  initialDate,
  initialTime,
  initialPostUuid,
}) => {
  const { servicesStore, toastStore, authStore, operatingHoursStore, postsStore, bookingsStore } = useStores();

  // === Form state ===
  const [phone, setPhone] = useState('+7');
  const [clientName, setClientName] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [selectedMake, setSelectedMake] = useState<SelectOption | null>(null);
  const [selectedModel, setSelectedModel] = useState<SelectOption | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [selectedService, setSelectedService] = useState<SelectOption | null>(null);
  const [selectedPost, setSelectedPost] = useState<SelectOption | null>(null);
  const [selectedAdditionalServices, setSelectedAdditionalServices] = useState<MultiSelectOption[]>([]);
  const [comment, setComment] = useState('');

  // === Client/Car state (create mode) ===
  const [selectedClient, setSelectedClient] = useState<ClientSearchResultDto | null>(null);
  const [selectedCar, setSelectedCar] = useState<CarSearchResultDto | null>(null);
  const [carDetailsModified, setCarDetailsModified] = useState(false);
  const [clientCarsOptions, setClientCarsOptions] = useState<AutocompleteOption[]>([]);

  // === UI state ===
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [timeError, setTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [overlapConfirmOpen, setOverlapConfirmOpen] = useState(false);
  const overlapAllowedRef = useRef(false);
  // === Refs ===
  const isFirstLoad = useRef(true);
  const mouseDownOnOverlay = useRef(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const formBodyRef = useRef<HTMLDivElement>(null);
  const overlapConfirmRef = useRef<HTMLDivElement>(null);
  const hasAutoSelectedService = useRef(false);
  const hasAutoSelectedPost = useRef(false);
  const autoSelectedPostBookingUuid = useRef<string | null>(null);

  // Хелпер для проверки readonly
  const isFieldReadOnly = useCallback(
    (field: BookingFormFieldName) => readonlyFields.has(field),
    [readonlyFields]
  );

  // === Инициализация формы ===
  useEffect(() => {
    if (!isOpen) return;

    isFirstLoad.current = true;
    hasAutoSelectedService.current = false;
    hasAutoSelectedPost.current = false;
    autoSelectedPostBookingUuid.current = null;
    overlapAllowedRef.current = false;
    setOverlapConfirmOpen(false);
    setEndTimeError('');

    if (mode === 'edit' && booking) {
      // Предзаполнение из данных бронирования
      setPhone(booking.client?.phone || '');
      setClientName(formatClientName(booking.client?.name));
      setLicensePlate(extractLicensePlate(booking.car?.license_plate));

      // Марка и модель (read-only в edit, но отображаются)
      if (booking.car?.make) {
        setSelectedMake({ label: booking.car.make, value: booking.car.make });
      }
      if (booking.car?.model) {
        setSelectedModel({ label: booking.car.model, value: booking.car.model });
      }

      // Дата и время
      const startTime = parseISO(booking.start_time);
      const endTime = parseISO(booking.end_time);
      setSelectedDate(startTime);
      setSelectedTime(format(startTime, 'HH:mm'));
      setSelectedEndTime(format(endTime, 'HH:mm'));

      // Услуга
      setSelectedService({
        label: booking.service.name,
        value: booking.service.uuid,
      });

      // Дополнительные услуги
      if (booking.additionalServices) {
        setSelectedAdditionalServices(
          booking.additionalServices.map(s => ({ label: s.name, value: s.uuid }))
        );
      } else {
        setSelectedAdditionalServices([]);
      }

      // Комментарий
      setComment(safeString(booking.client_comment));
    } else {
      // Режим создания
      setPhone('+7');
      setClientName('');
      setLicensePlate('');
      setSelectedMake(null);
      setSelectedModel(null);
      setSelectedDate(initialDate || null);
      setSelectedTime(initialTime || '');
      setSelectedEndTime('');
      setSelectedService(null);
      setSelectedPost(null);
      setSelectedAdditionalServices([]);
      setComment('');
      setSelectedClient(null);
      setSelectedCar(null);
      setCarDetailsModified(false);
      setClientCarsOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode]);

  // === Загрузка услуг ===
  useEffect(() => {
    if (isOpen && servicesStore.services.length === 0) {
      servicesStore.fetchServices();
    }
  }, [isOpen, servicesStore]);

  // Авто-выбор первой услуги (только в create mode)
  useEffect(() => {
    if (isOpen) {
      hasAutoSelectedService.current = false;
      hasAutoSelectedPost.current = false;
      autoSelectedPostBookingUuid.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      mode === 'create' &&
      servicesStore.mainServices.length > 0 &&
      !selectedService &&
      !hasAutoSelectedService.current
    ) {
      const firstService = servicesStore.mainServices[0];
      setSelectedService({ label: firstService.name, value: firstService.uuid });
      hasAutoSelectedService.current = true;
    }
  }, [isOpen, mode, servicesStore.mainServices, selectedService]);

  const createTimeSlots = useMemo(() => {
    if (!selectedDate) return [];

    const workingHours = getWorkingHoursForDate(
      selectedDate,
      operatingHoursStore.regularSchedule,
      operatingHoursStore.specialDates,
    );
    if (!workingHours) return [];

    return buildTimeSlots(
      parseTimeToMinutes(workingHours.open),
      parseTimeToMinutes(workingHours.close),
      15,
    );
  }, [
    operatingHoursStore.regularSchedule,
    operatingHoursStore.specialDates,
    selectedDate,
  ]);

  useEffect(() => {
    setAvailableTimeSlots(createTimeSlots);

    if (mode === 'edit') {
      if (createTimeSlots.length > 0) {
        isFirstLoad.current = false;
      }
      return;
    }

    if (!isFirstLoad.current) {
      setSelectedTime((current) => {
        if (!current) return '';
        if (createTimeSlots.includes(current)) return current;
        return '';
      });
    } else if (createTimeSlots.length > 0) {
      isFirstLoad.current = false;
    }
  }, [createTimeSlots, mode]);

  // === Опции для селектов ===
  const mainServiceOptions: SelectOption[] = useMemo(
    () => servicesStore.mainServices.map(s => ({ label: s.name, value: s.uuid })),
    [servicesStore.mainServices]
  );

  const additionalServiceOptions: MultiSelectOption[] = useMemo(
    () => servicesStore.additionalServices.map(s => ({ label: s.name, value: s.uuid })),
    [servicesStore.additionalServices]
  );

  const postOptions: SelectOption[] = useMemo(
    () =>
      postsStore.posts
        .slice()
        .sort((a, b) => a.orderNumber - b.orderNumber)
        .map(post => ({ label: `Пост ${post.orderNumber}`, value: post.uuid })),
    [postsStore.posts]
  );

  const editStartTimeSlots = useMemo(() => {
    if (mode !== 'edit' || !selectedDate) return [];

    const workingHours = getWorkingHoursForDate(
      selectedDate,
      operatingHoursStore.regularSchedule,
      operatingHoursStore.specialDates,
    );
    if (!workingHours) return [];

    const slots = buildTimeSlots(
      parseTimeToMinutes(workingHours.open),
      parseTimeToMinutes(workingHours.close),
      15,
    );

    if (selectedTime && !slots.includes(selectedTime)) {
      return [...slots, selectedTime].sort(
        (a, b) => parseTimeToMinutes(a) - parseTimeToMinutes(b),
      );
    }

    return slots;
  }, [
    mode,
    operatingHoursStore.regularSchedule,
    operatingHoursStore.specialDates,
    selectedDate,
    selectedTime,
  ]);

  const endTimeSlots = useMemo(() => {
    if (!selectedDate || !selectedTime) return [];

    const workingHours = getWorkingHoursForDate(
      selectedDate,
      operatingHoursStore.regularSchedule,
      operatingHoursStore.specialDates,
    );
    if (!workingHours) return [];

    return buildTimeSlots(
      parseTimeToMinutes(selectedTime) + 15,
      parseTimeToMinutes(workingHours.close),
      15,
    );
  }, [
    operatingHoursStore.regularSchedule,
    operatingHoursStore.specialDates,
    selectedDate,
    selectedTime,
  ]);

  // Авто-выбор поста
  useEffect(() => {
    if (!isOpen || postOptions.length === 0) return;

    if (mode === 'create') {
      if (initialPostUuid) {
        const matchingPost = postOptions.find(p => p.value === initialPostUuid);
        setSelectedPost(matchingPost || postOptions[0]);
      } else if (!selectedPost) {
        setSelectedPost(postOptions[0]);
      }
      return;
    }

    if (mode === 'edit' && booking) {
      if (autoSelectedPostBookingUuid.current !== booking.uuid) {
        hasAutoSelectedPost.current = false;
        autoSelectedPostBookingUuid.current = booking.uuid;
      }
      if (hasAutoSelectedPost.current) return;

      const bookingPostUuid = getBookingPostUuid(booking);
      const matchingPost = bookingPostUuid
        ? postOptions.find((post) => post.value === bookingPostUuid)
        : undefined;

      // Не подставляем «Пост 1», если у записи другой пост или uuid ещё не известен
      if (!matchingPost) return;

      setSelectedPost(matchingPost);
      hasAutoSelectedPost.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, postOptions, initialPostUuid, mode, booking]);

  // === Блокировка скролла ===
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!overlapConfirmOpen) return;

    requestAnimationFrame(() => {
      const body = formBodyRef.current;
      if (body) {
        body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
      }
      overlapConfirmRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    });
  }, [overlapConfirmOpen]);

  // === Обработчики ===
  const handleTimeChange = useCallback((newTime: string) => {
    if (mode === 'edit' && selectedTime && selectedEndTime) {
      const previousStart = parseTimeToMinutes(selectedTime);
      const previousEnd = parseTimeToMinutes(selectedEndTime);
      const duration = Math.max(15, previousEnd - previousStart);
      const nextStart = parseTimeToMinutes(newTime);
      let nextEnd = nextStart + duration;

      if (selectedDate) {
        const workingHours = getWorkingHoursForDate(
          selectedDate,
          operatingHoursStore.regularSchedule,
          operatingHoursStore.specialDates,
        );
        if (workingHours) {
          const closeMinutes = parseTimeToMinutes(workingHours.close);
          nextEnd = Math.min(nextEnd, closeMinutes);
          if (nextEnd - nextStart < 15) {
            nextEnd = Math.min(closeMinutes, nextStart + 15);
          }
        }
      }

      setSelectedEndTime(formatMinutesToTime(nextEnd));
      setEndTimeError('');
    }

    setSelectedTime(newTime);
    if (timeError) setTimeError('');
  }, [
    mode,
    operatingHoursStore.regularSchedule,
    operatingHoursStore.specialDates,
    selectedDate,
    selectedEndTime,
    selectedTime,
    timeError,
  ]);

  const handleEndTimeChange = useCallback((newTime: string) => {
    setSelectedEndTime(newTime);
    if (endTimeError) setEndTimeError('');
  }, [endTimeError]);

  const handleDateChange = useCallback((newDate: Date | null) => {
    setSelectedDate(newDate);
    if (timeError) setTimeError('');

    if (newDate && selectedTime) {
      const workingHours = getWorkingHoursForDate(
        newDate,
        operatingHoursStore.regularSchedule,
        operatingHoursStore.specialDates
      );

      if (!workingHours) {
        setSelectedTime('');
        return;
      }

      if (selectedTime < workingHours.open || selectedTime >= workingHours.close) {
        setSelectedTime('');
      }
    }
  }, [timeError, selectedTime, operatingHoursStore.regularSchedule, operatingHoursStore.specialDates]);

  // === Dirty check ===
  const isFormDirty = useCallback(() => {
    if (mode === 'edit') return true; // В режиме редактирования всегда считаем dirty

    const hasPhoneChange = phone !== '+7' && phone !== '';
    const hasLicensePlate = licensePlate.trim() !== '';
    const hasClientName = clientName.trim() !== '';
    const hasComment = comment.trim() !== '';
    const hasAdditionalServices = selectedAdditionalServices.length > 0;

    return hasPhoneChange || hasLicensePlate || hasClientName || hasComment || hasAdditionalServices;
  }, [mode, phone, licensePlate, clientName, comment, selectedAdditionalServices]);

  const handleCloseWithConfirmation = useCallback(() => {
    if (mode === 'create' && isFormDirty()) {
      if (window.confirm('Вы уверены, что хотите закрыть окно? Все несохранённые данные будут потеряны.')) {
        onClose();
      }
    } else {
      onClose();
    }
  }, [mode, isFormDirty, onClose]);

  // Overlay click handling
  const handleOverlayMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      mouseDownOnOverlay.current = true;
    }
  }, []);

  const handleOverlayMouseUp = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current && mouseDownOnOverlay.current) {
      handleCloseWithConfirmation();
    }
    mouseDownOnOverlay.current = false;
  }, [handleCloseWithConfirmation]);

  // === Валидация и отправка ===
  const handleSubmit = async () => {
    // Валидация полей, которые не readonly
    if (!isFieldReadOnly('phone') && !isPhoneComplete(phone)) {
      setPhoneError('Введите номер телефона полностью');
      toastStore.showError('Введите номер телефона полностью');
      return;
    }

    if (!isFieldReadOnly('date') && !selectedDate) {
      toastStore.showError('Выберите дату');
      return;
    }

    if (!isFieldReadOnly('time') && !selectedTime) {
      toastStore.showError('Выберите время');
      return;
    }

    // Проверка даты/времени в рабочих часах (для create и editable time)
    if (!isFieldReadOnly('time') && selectedDate && selectedTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const bookingDateTime = new Date(selectedDate);
      bookingDateTime.setHours(hours, minutes, 0, 0);

      if (mode === 'create' && bookingDateTime < new Date()) {
        toastStore.showError('Нельзя создать запись на прошедшее время');
        return;
      }

      const workingHours = getWorkingHoursForDate(
        selectedDate,
        operatingHoursStore.regularSchedule,
        operatingHoursStore.specialDates
      );

      if (!workingHours) {
        setTimeError('В этот день сервисный центр не работает');
        toastStore.showError('В этот день сервисный центр не работает');
        return;
      }

      if (selectedTime < workingHours.open || selectedTime >= workingHours.close) {
        setTimeError(`Доступное время: ${workingHours.open}-${workingHours.close}`);
        toastStore.showError(`Недоступное время. Рабочие часы: ${workingHours.open}-${workingHours.close}`);
        return;
      }

      if (mode === 'edit') {
        if (!selectedEndTime) {
          setEndTimeError('Выберите время окончания');
          toastStore.showError('Выберите время окончания');
          return;
        }

        const startMinutes = parseTimeToMinutes(selectedTime);
        const endMinutes = parseTimeToMinutes(selectedEndTime);
        if (endMinutes - startMinutes < 15) {
          setEndTimeError('Минимальная длительность записи — 15 минут');
          toastStore.showError('Минимальная длительность записи — 15 минут');
          return;
        }

        if (selectedEndTime > workingHours.close) {
          setEndTimeError(`Окончание не позже ${workingHours.close}`);
          toastStore.showError(`Окончание не позже ${workingHours.close}`);
          return;
        }
      }
    }

    if (!isFieldReadOnly('service') && !selectedService) {
      toastStore.showError('Выберите услугу');
      return;
    }

    if (!isFieldReadOnly('post') && !selectedPost) {
      toastStore.showError('Выберите пост');
      return;
    }

    setIsSubmitting(true);
    let resolvedClient = selectedClient;

    try {
      if (selectedDate && selectedTime) {
        let clientUuid: string | undefined;
        if (mode === 'edit') {
          clientUuid = booking?.client?.uuid ?? booking?.client_uuid;
        } else if (isPhoneComplete(phone)) {
          try {
            const existingClient = selectedClient ?? (await findClientByExactPhone(phone));
            if (existingClient && !selectedClient) {
              setSelectedClient(existingClient);
            }
            resolvedClient = existingClient;
            clientUuid = existingClient?.uuid;
          } catch {
            clientUuid = selectedClient?.uuid;
          }
        }

        if (clientUuid) {
          const startTime = new Date(selectedDate);
          const [startHours, startMinutes] = selectedTime.split(':').map(Number);
          startTime.setHours(startHours, startMinutes, 0, 0);

          let endTime: Date;
          if (mode === 'edit' && selectedEndTime) {
            const [endHours, endMinutes] = selectedEndTime.split(':').map(Number);
            endTime = new Date(selectedDate);
            endTime.setHours(endHours, endMinutes, 0, 0);
          } else {
            const serviceUuid = selectedService ? String(selectedService.value) : '';
            const mainDuration = servicesStore.services.find((service) => service.uuid === serviceUuid)
              ?.duration_minutes;
            const additionalDurations = selectedAdditionalServices.map((item) => {
              const uuid = item.value == null ? '' : String(item.value);
              return servicesStore.additionalServices.find((service) => service.uuid === uuid)
                ?.duration_minutes;
            });
            const durationMinutes = getServicesDurationMinutes(mainDuration, additionalDurations);
            endTime = new Date(startTime.getTime() + durationMinutes * 60_000);
          }

          const carUuid =
            selectedCar?.uuid
            ?? booking?.car?.uuid
            ?? (typeof booking?.car_uuid === 'string' ? booking.car_uuid : undefined);

          const carLicensePlate =
            licensePlate.trim()
            || selectedCar?.license_plate
            || extractLicensePlate(booking?.car?.license_plate)
            || undefined;

          const serviceCenterUuid = authStore.user?.service_center_uuid;
          let overlapBookings = [...bookingsStore.bookings, ...bookingsStore.pendingBookings];

          if (serviceCenterUuid) {
            try {
              const dayBookings = await fetchBookingsInRange({
                serviceCenterUuid,
                dateFrom: startOfDay(selectedDate),
                dateTo: endOfDay(selectedDate),
              });
              overlapBookings = mergeBookingsByUuid(dayBookings, bookingsStore.pendingBookings);
            } catch {
              toastStore.showError('Не удалось проверить пересечение записей клиента');
              return;
            }
          }

          const overlap = detectClientOverlapOnOtherPost(
            overlapBookings,
            {
              clientUuid,
              carUuid,
              carLicensePlate,
              start: startTime,
              end: endTime,
              postUuid: selectedPost ? String(selectedPost.value) : undefined,
              excludeBookingUuid: mode === 'edit' ? booking?.uuid : undefined,
            },
          );

          if (overlap?.kind === 'app_block') {
            toastStore.showError(
              'Клиент уже записан из приложения на это время с этим автомобилем. Выберите другую машину или время.',
            );
            return;
          }

          if (
            mode === 'create'
            && overlap?.kind === 'admin_confirm'
            && !overlapAllowedRef.current
          ) {
            setOverlapConfirmOpen(true);
            return;
          }
        }
      }

      overlapAllowedRef.current = false;

      await onSubmit({
        phone,
        clientName,
        licensePlate,
        selectedMake,
        selectedModel,
        selectedDate,
        selectedTime,
        selectedEndTime,
        selectedService,
        selectedPost,
        selectedAdditionalServices,
        comment,
        selectedClient: resolvedClient,
        selectedCar,
        carDetailsModified,
      });
    } catch {
      // Ошибки обрабатываются в onSubmit parent
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="booking-form__overlay"
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
      data-testid={mode === 'create' ? 'create-booking-modal' : 'edit-booking-modal'}
    >
      <div className="booking-form__container" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="booking-form__header" data-testid="booking-form-header">
          <div className="booking-form__header-content">
            <h2 className="booking-form__title">{title}</h2>
            {headerExtra}
          </div>
          <button
            className="booking-form__close"
            onClick={handleCloseWithConfirmation}
            aria-label="Закрыть"
            disabled={isSubmitting}
            data-testid="booking-form-cancel"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="booking-form__body" ref={formBodyRef}>
          {/* Клиент: телефон + имя */}
          <ClientPhoneField
            phone={mode === 'edit' ? formatPhoneWithMask((booking?.client?.phone || '').replace(/\D/g, '')) : phone}
            clientName={clientName}
            onPhoneChange={setPhone}
            onClientNameChange={setClientName}
            selectedClient={selectedClient}
            onClientSelect={setSelectedClient}
            onCarsLoaded={setClientCarsOptions}
            disabled={isFieldReadOnly('phone')}
            required
            error={phoneError}
            onErrorClear={() => setPhoneError('')}
          />

          {/* Автомобиль: номер + марка/модель */}
          <CarField
            licensePlate={licensePlate}
            onLicensePlateChange={setLicensePlate}
            selectedMake={selectedMake}
            onMakeChange={setSelectedMake}
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            selectedCar={selectedCar}
            onCarSelect={setSelectedCar}
            onCarDetailsModified={setCarDetailsModified}
            clientUuid={selectedClient?.uuid}
            clientCarsOptions={clientCarsOptions}
            disabled={isFieldReadOnly('licensePlate')}
          />

          {mode === 'edit' ? (
            <>
              <div className="booking-form__field" data-testid="booking-date-input">
                <AppDatePicker
                  label="Дата"
                  value={selectedDate}
                  onChange={handleDateChange}
                  disabled={isFieldReadOnly('date')}
                />
              </div>
              <div className="booking-form__field-row">
                <div className="booking-form__field" data-testid="booking-time-input">
                  <AppTimePicker
                    label="Начало"
                    value={selectedTime}
                    onChange={handleTimeChange}
                    placeholder="09:00"
                    availableSlots={editStartTimeSlots}
                    disabled={isFieldReadOnly('time') || !selectedDate}
                    error={timeError}
                  />
                </div>
                <div className="booking-form__field" data-testid="booking-end-time-input">
                  <AppTimePicker
                    label="Окончание"
                    value={selectedEndTime}
                    onChange={handleEndTimeChange}
                    placeholder="10:00"
                    availableSlots={endTimeSlots}
                    disabled={isFieldReadOnly('time') || !selectedDate || !selectedTime}
                    error={endTimeError}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="booking-form__field-row">
              <div className="booking-form__field" data-testid="booking-date-input">
                <AppDatePicker
                  label="Дата"
                  value={selectedDate}
                  onChange={handleDateChange}
                  minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                  disabled={isFieldReadOnly('date')}
                />
              </div>

              <div className="booking-form__field" data-testid="booking-time-input">
                <AppTimePicker
                  label="Время"
                  value={selectedTime}
                  onChange={handleTimeChange}
                  placeholder="09:00"
                  availableSlots={availableTimeSlots}
                    disabled={isFieldReadOnly('time') || !selectedDate}
                  error={timeError}
                />
              </div>
            </div>
          )}

          {/* Услуга */}
          <div className="booking-form__field" data-testid="booking-service-select">
            <AppSingleSelect
              label="Название услуги"
              placeholder="Выберите услугу"
              options={mainServiceOptions}
              value={selectedService}
              onChange={setSelectedService}
              clearable
              disabled={isFieldReadOnly('service')}
            />
          </div>

          {/* Пост */}
          <div className="booking-form__field" data-testid="booking-post-select">
            <AppSingleSelect
              label="Пост"
              placeholder="Выберите пост"
              options={postOptions}
              value={selectedPost}
              onChange={setSelectedPost}
              disabled={isFieldReadOnly('post') || postOptions.length === 0}
            />
          </div>

          {/* Дополнительные услуги */}
          <div className="booking-form__field" data-testid="booking-additional-services-select">
            <AppMultiSelect
              label="Дополнительные услуги"
              placeholder="Выберите дополнительные услуги"
              options={additionalServiceOptions}
              value={selectedAdditionalServices}
              onChange={setSelectedAdditionalServices}
              clearable
              multiline
              disabled={isFieldReadOnly('additionalServices')}
            />
          </div>

          {/* Комментарий */}
          <div className="booking-form__field" data-testid="booking-comment-input">
            <AppTextarea
              label="Комментарий к заказу"
              placeholder="Введите комментарий"
              value={comment}
              onChange={setComment}
              minRows={2}
              maxRows={10}
              autoResize
              disabled={isFieldReadOnly('comment')}
            />
          </div>

          {/* Информационное сообщение для ограниченного редактирования */}
          {mode === 'edit' &&
            booking?.created_by_source === 'client_app' &&
            isFieldReadOnly('time') && (
            <div className="booking-form__info">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10 6.5V10.5M10 13.5H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                  stroke="#B2B1AE"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Для броней из клиентского приложения доступно изменение только поста и комментария</span>
            </div>
          )}
          {mode === 'create' && overlapConfirmOpen && (
            <div ref={overlapConfirmRef}>
              <AppAlert
                message="Этот клиент уже записан на другой пост в это время. Вы уверены, что хотите записать?"
                confirmText="Записать"
                cancelText="Отмена"
                onConfirm={() => {
                  overlapAllowedRef.current = true;
                  setOverlapConfirmOpen(false);
                  void handleSubmit();
                }}
                onCancel={() => {
                  overlapAllowedRef.current = false;
                  setOverlapConfirmOpen(false);
                }}
                data-testid="booking-client-overlap-confirm"
              />
            </div>
          )}
        </div>
        <div className="booking-form__footer" data-testid="booking-form-footer">
          <AppButton
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
            data-testid="booking-form-submit"
          >
            {submitLabel}
          </AppButton>
        </div>
      </div>
    </div>
  );
});

BookingForm.displayName = 'BookingForm';

export default BookingForm;
